import {
  ViewportScroller,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { catchError, forkJoin, of, Subscription, take } from 'rxjs';
import { StateWiseDistribution } from 'src/app/components/sections/state-wise-distribution/state-wise-distribution.component';
import { ChartFullscreenComponent } from 'src/app/components/utility/chart-fullscreen/chart-fullscreen.component';
import { LINEGRAPHCONFIG } from 'src/app/constants/chart.config';
import { states } from 'src/app/constants/states';
import { UserStats } from 'src/app/models/umang-stats.model';
import { CachingService } from 'src/app/services/caching.service';
import { CallAPIService } from 'src/app/services/call-api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { Request } from 'express';
import { LanguageHandlerService } from 'src/app/services/language-handler.service';

export interface Department {
  sno: number;
  deptname: string;
}

export interface Service {
  sno: number;
  servname: string;
}

export interface DepartmentData {
  department: Department[];
  year: number;
  month: string;
  reportname: string;
}

export interface ServiceData {
  reportname: string;
  data: Service[];
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  dashboardSafeUrl!: SafeResourceUrl;
  states = states;
  scroll!: string;
  dashboardUrl =
    'https://dashboard.umangapp.in/public/dashboards/TyB4gvXZeymAftxgBBcHl5ordGSyzov2DApQIFB8?org_slug=default';
  private title = 'UMANG Dashboard';
  private description =
    'UMANG provides complete stats of all the services presents on platform in terms of Registered users, Daily services hit counts, geographical presence etc.';
  public statData: any;
  deptList: Department[] = [];
  serviceList: Service[] = [];
  public userStats: any;
  public transStats: any;
  public serviceStats: any;
  public stateWiseStats: any;
  public cumlativeDepartment: any;
  public stateWiseDeptServstats: any;
  public years: any[] = [];
  public currentState = '0';
  public lineConfig = LINEGRAPHCONFIG;
  public today = new Date();
  public docScheme: any = {};
  public priorDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth() - 1,
    this.today.getDate()
  );
  @ViewChild('stateWise') stateWiseComponent!: StateWiseDistribution;
  serviceIntegrated: any;
  public headFootVisible: boolean = true;
  public loader: boolean = true;
  public isPlatformBrowser
  private queryParamsSubs?: Subscription;
  langChangeSubs!:Subscription;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Optional() @Inject(REQUEST) private request: Request,
    private titleService: Title,
    private metaService: Meta,
    private viewportScroller: ViewportScroller,
    private apiService: CallAPIService,
    private cacheService: CachingService,
    private dialog: MatDialog,
    private utilService: UtilsService,
    private router: Router,
    private langHandlerService: LanguageHandlerService
  ) {}

  ngOnInit() {
    this.isPlatformBrowser = isPlatformBrowser(this.platformId);
    setTimeout(() => {
      this.loader = false;
    }, 3000);
    this.setTitleAndMeta();
    this.checkViewportSize();
    // this.dashboardSafeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
    //   this.dashboardUrl
    // );
    this.callApis();
    this.addLangChangeListener();
    if (isPlatformServer(this.platformId)) {
      this.headFootVisible = !this.request.url.endsWith('/dashboard/shared');
    } else {
      this.headFootVisible = !this.router.url.endsWith('/dashboard/shared');
    }
  }

  redirectTORoute(route:string) {
    this.router.navigateByUrl(route);
  }

  callApis() {
    this.generateYears();
    this.getUmangStats();
    this.getUserStats();
    this.getTransactionStats();
    this.getServicesStats();
    this.getStateWiseData();
    this.fethstateWiseDeptServstats();
    this.fetchStatsRecord();
    this.getDeptServiceStats();
  }

  private addLangChangeListener() {
    this.langChangeSubs = this.langHandlerService.onLangChange.subscribe(() => {
      this.callApis();
    });
  }

  private setTitleAndMeta() {
    this.titleService.setTitle(this.title);
    this.metaService.updateTag({
      name: 'description',
      content: this.description,
    });
  }

  @HostListener('window:resize') resizeListener() {
    this.checkViewportSize();
  }

  private checkViewportSize() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.scroll = window?.innerWidth <= 890 ? '' : 'no';
  }

  showChartFullScreen(stats: any, heading: string, id: string) {
    this.dialog.open(ChartFullscreenComponent, {
      data: {
        stats: stats,
        config: this.lineConfig,
        heading: heading,
        id,
      },
      panelClass: 'chart-fullscreen',
    });
  }

  scrollToSection(sectionId: string) {
    this.viewportScroller.scrollToAnchor(sectionId);
  }

  getUmangStats() {
    const join = [this.apiService.getUmangStats(), this.apiService.getCount()];
    forkJoin(join).subscribe({
      next: (res: any) => {
        console.log(res);
        this.statData = res[0]?.data[0];
        this.statData['lastApiCallTime'] = Date.now();
        this.statData['totalschemes'] = res[1]?.data?.scheme;
        this.statData['issueddocument'] = res[1]?.data?.document;
        this.cacheService.storeDashboardData('umangStats', this.statData);
      },
      error: (err: any) => {
        this.statData = this.cacheService.fetchDashboardData('umangStats');
      },
    });
  }

  getUserStats() {
    this.apiService.getUserStats().subscribe({
      next: (resp: UserStats) => {
        this.userStats = resp?.usercount[0];
        this.userStats['lastApiCallTime'] = Date.now();
        this.cacheService.storeDashboardData('userStats', this.userStats);
      },
      error: (err: any) => {
        this.userStats = this.cacheService.fetchDashboardData('userStats');
      },
    });
  }

  getTransactionStats() {
    this.apiService.getTransactionStats().subscribe({
      next: (resp: UserStats) => {
        this.transStats = resp?.usercount[0];
        this.transStats['lastApiCallTime'] = Date.now();
        this.cacheService.storeDashboardData('trnasStats', this.transStats);
      },
      error: (err: any) => {
        this.transStats = this.cacheService.fetchDashboardData('trnasStats');
      },
    });
  }

  getServicesStats() {
    this.apiService.getServiceData().subscribe({
      next: (resp: any) => {
        this.serviceStats = resp?.usercount[0];
        this.serviceStats['lastApiCallTime'] = Date.now();
        this.cacheService.storeDashboardData('serviceStats', this.serviceStats);
      },
      error: (err: any) => {
        this.serviceStats =
          this.cacheService.fetchDashboardData('serviceStats');
      },
    });
  }

  fetchStatsRecord() {
    this.apiService.fetchStatsRecord().subscribe({
      next: (resp: any) => {
        this.serviceIntegrated = resp;
        this.serviceIntegrated['lastApiCallTime'] = Date.now();
        this.cacheService.storeDashboardData(
          'serviceIntegrated',
          this.serviceIntegrated
        );
      },
      error: (err: any) => {
        this.serviceIntegrated =
          this.cacheService.fetchDashboardData('serviceIntegrated');
      },
    });
  }
  getStateWiseData() {
    const join = [
      this.apiService.stateWiseCount(),
      this.apiService.stateWiseUser(),
    ];
    forkJoin(join).subscribe({
      next: (resp: any) => {
        this.stateWiseStats = resp[0];
        this.stateWiseStats['lastApiCallTime'] = Date.now();
        this.stateWiseStats.data.forEach((ele: any) => {
          const foundItem = resp[1][1].data.find(
            (data: any) => data.stateid === ele.id || data.stateid == '0'
          );
          if (foundItem) ele['totalUser'] = foundItem.totaluser;
          const serviceItem = resp[1][2]['servicecounts'][0]['data'].find(
            (ele: any) => ele?.stateid === foundItem?.stateid
          );
          if (serviceItem) ele['services'] = serviceItem?.count || 0;
          else ele['services'] = 0;
        });
        this.cacheService.storeDashboardData(
          'stateWiseStats',
          this.stateWiseStats
        );
      },
      error: (err: any) => {
        this.statData = this.cacheService.fetchDashboardData('stateWiseStats');
      },
    });
  }

  convertToTitleCase(str: string) {
    if (!str) {
      return '';
    }
    return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
  }

  download() {
    const newArray = this.stateWiseStats?.data.map((ele: any) => {
      return {
        State: ele?.displayName.toUpperCase(),
        Users: ele?.totalUser || '0',
        Applications: ele?.departments,
        Services: ele?.services,
        Schemes: ele?.schemes,
        Documents: ele?.documents,
      };
    });
    const totalObj = {
      State: 'TOTAL',
      Users: this.stateWiseComponent?.total?.users || '0',
      Applications: this.stateWiseComponent?.total?.department,
      Services: this.stateWiseComponent?.total?.service,
      Schemes: this.stateWiseComponent?.total?.schemes,
      Documents: this.stateWiseComponent?.total?.documents,
    };
    newArray.push(totalObj);
    this.utilService.downloadCSV(newArray);
  }

  fethstateWiseDeptServstats() {
    this.apiService.getstateWiseDeptServstats().subscribe({
      next: (resp: any) => {
        this.stateWiseDeptServstats = resp?.usercount[0];
        this.stateWiseDeptServstats['lastApiCallTime'] = Date.now();
        this.cacheService.storeDashboardData(
          'stateWiseDeptServstats',
          this.stateWiseDeptServstats
        );
      },
      error: (err: any) => {
        this.stateWiseDeptServstats = this.cacheService.fetchDashboardData(
          'stateWiseDeptServstats'
        );
      },
    });
  }

  onstateChanges(event: any) {
    this.currentState = event.value.toString();
    this.stateWiseComponent.onStateChange(this.currentState);
  }

  generateYears() {
    for (let i = 2017; i <= new Date().getFullYear(); i++) {
      this.years.push(i);
    }
  }

  getDeptServiceStats(): void {
    this.apiService
      .getTopDeptServices()
      .pipe(
        take(1),
        catchError((error) => {
          const cachedData =
            this.cacheService.fetchDashboardData('twentyDeptSrvStats');
          if (cachedData) {
            this.deptList = cachedData.deptList;
            this.serviceList = cachedData.serviceList;
          }
          return of(null);
        })
      )
      .subscribe((resp: any) => {
        if (resp) {
          const [, departmentData, serviceDataWrapper] = resp;
          this.deptList = departmentData?.department || [];
          this.serviceList = serviceDataWrapper?.service[0]?.data || [];
          this.cacheService.storeDashboardData('twentyDeptSrvStats', {
            deptList: this.deptList,
            serviceList: this.serviceList,
          });
        }
      });
  }

  SchemeDoc(event: any) {
    console.log(event)
    this.docScheme['schemes'] = event.schemes;
    this.docScheme['schemesCenter'] = event.centerData.schemes;
    this.docScheme['documents'] = event.documents;
    this.docScheme['documentsCenter'] = +event.centerData.documents;
    // console.log(this.docScheme['documents'])
    console.log(this.docScheme['schemes']);
    console.log(this.docScheme['schemesCenter']);
    console.log(this.docScheme['documents']);
    console.log(this.docScheme['documentsCenter']);
  }

  redirectToUmang() {
    window.open('https://web.umang.gov.in/landing', '_blank');
  }

  ngOnDestroy() {
    this.queryParamsSubs?.unsubscribe();
  }
}
